import { CalendarDays, Paperclip } from "lucide-react";
import { useId, useRef, useState } from "react";
import { Icon } from "../../icons/Icon";
import { Button } from "../../primitives/Button";
import { cx, DISABLED, FOCUS_RING, TOUCH_TARGET } from "../../styles";

/**
 * A date, as a plain calendar day.
 *
 * The value is `YYYY-MM-DD` and never an instant. A delivery window, a stock
 * take, a billing period — these are days in the tenant's own calendar, and
 * turning one into a timestamp is how "the 1st" becomes "the 31st at 22:00" for
 * whoever opens it from another timezone. `lib/dates` formats *instants* in a
 * zone; this deals in days, which have no zone to be in.
 *
 * The native `<input type="date">` is the control, in both directions and at
 * every width. That is a deliberate choice against the design's desktop
 * calendar popover, and it is recorded as a deviation rather than hidden: the
 * native picker already speaks the person's locale, already opens a real
 * calendar on a phone, is already keyboard-accessible, and already handles the
 * Gregorian/Hijri question an Arabic tenant will eventually raise. A
 * hand-built calendar would be a month of work to reach parity and a permanent
 * source of `role="grid"` bugs. When a range picker is genuinely needed — the
 * reporting epic — that is the story to build it in, against a real screen.
 */

export interface DatePickerProps {
	/** `YYYY-MM-DD`, or null. */
	value: string | null;
	onChange: (value: string | null) => void;
	/** Earliest selectable day, `YYYY-MM-DD`. */
	min?: string;
	max?: string;
	disabled?: boolean;
	id?: string;
	name?: string;
	"aria-label"?: string | undefined;
	"aria-describedby"?: string | undefined;
	"aria-invalid"?: true | undefined;
	className?: string;
}

export function DatePicker({
	value,
	onChange,
	min,
	max,
	disabled = false,
	id,
	name,
	className,
	"aria-label": ariaLabel,
	"aria-describedby": describedBy,
	"aria-invalid": invalid,
}: DatePickerProps) {
	return (
		<div
			className={cx(
				"flex h-9 items-center gap-2 rounded-md border border-border-strong bg-bg-surface px-2.5",
				"focus-within:ring-3 focus-within:ring-ring-halo focus-within:border-ring",
				"has-[input[aria-invalid]]:border-danger-border has-[input[aria-invalid]]:ring-3 has-[input[aria-invalid]]:ring-danger-bg",
				TOUCH_TARGET,
				DISABLED,
				className,
			)}
		>
			<Icon as={CalendarDays} size={16} className="shrink-0 text-fg-subtle" />
			<input
				type="date"
				id={id}
				name={name}
				value={value ?? ""}
				min={min}
				max={max}
				disabled={disabled}
				aria-label={ariaLabel}
				aria-describedby={describedBy}
				aria-invalid={invalid}
				// An empty field is `null`, not `""`. The API distinguishes "no date"
				// from "a date I could not parse", and so should this.
				onChange={(event) => onChange(event.target.value === "" ? null : event.target.value)}
				className={cx(
					"min-w-0 flex-1 bg-transparent text-[13px] text-fg outline-none",
					"[font-variant-numeric:tabular-nums]",
					"disabled:cursor-not-allowed disabled:text-fg-subtle",
				)}
			/>
		</div>
	);
}

export interface Period {
	from: string | null;
	to: string | null;
}

export interface PeriodPickerProps {
	value: Period;
	onChange: (value: Period) => void;
	fromLabel: string;
	toLabel: string;
	/** Shown when the end is before the start. */
	invertedLabel?: string;
	disabled?: boolean;
	className?: string;
}

/**
 * A start and an end, with the one rule that matters between them.
 *
 * The end cannot be before the start, and that is enforced by narrowing the
 * second field's `min` rather than by validating afterwards: a picker that lets
 * somebody choose an impossible range and then tells them off is a picker that
 * made them do work twice. The message is still rendered, for the case where a
 * value arrives from outside — a saved filter, a URL.
 */
export function PeriodPicker({
	value,
	onChange,
	fromLabel,
	toLabel,
	invertedLabel,
	disabled = false,
	className,
}: PeriodPickerProps) {
	const inverted = value.from !== null && value.to !== null && value.to < value.from;

	return (
		<div className={cx("flex flex-col gap-1", className)}>
			<div className="flex flex-wrap items-center gap-2">
				<DatePicker
					value={value.from}
					onChange={(from) => onChange({ ...value, from })}
					aria-label={fromLabel}
					disabled={disabled}
					{...(value.to === null ? {} : { max: value.to })}
					className="min-w-36 flex-1"
				/>
				<span aria-hidden="true" className="text-fg-subtle">
					–
				</span>
				<DatePicker
					value={value.to}
					onChange={(to) => onChange({ ...value, to })}
					aria-label={toLabel}
					disabled={disabled}
					{...(value.from === null ? {} : { min: value.from })}
					className="min-w-36 flex-1"
				/>
			</div>
			{inverted && invertedLabel !== undefined && (
				<p role="alert" className="text-[11.5px] text-danger-fg">
					{invertedLabel}
				</p>
			)}
		</div>
	);
}

export interface FileInputProps {
	onChange: (file: File | null) => void;
	/** A list of extensions or media types, as the `accept` attribute takes them. */
	accept?: string;
	/** The button's text. */
	chooseLabel: string;
	/** What is shown when nothing has been chosen. */
	emptyLabel: string;
	clearLabel?: string;
	disabled?: boolean;
	id?: string;
	name?: string;
	"aria-describedby"?: string | undefined;
	className?: string;
}

/**
 * One file, chosen through a real `<input type="file">`.
 *
 * The input is visually hidden rather than replaced, and that is the difference
 * between a control that works and one that looks like it does: a styled `div`
 * with a click handler cannot open a file dialog from the keyboard, is not
 * announced as a file input, and drops out of a form's data entirely. The
 * button is a `<label>` for it, so pressing Enter on the button opens the
 * chooser the same way clicking does.
 */
export function FileInput({
	onChange,
	accept,
	chooseLabel,
	emptyLabel,
	clearLabel,
	disabled = false,
	id,
	name,
	className,
	"aria-describedby": describedBy,
}: FileInputProps) {
	const generated = useId();
	const inputId = id ?? generated;
	const input = useRef<HTMLInputElement>(null);
	const [chosen, setChosen] = useState<File | null>(null);

	function choose(file: File | null) {
		setChosen(file);
		onChange(file);
	}

	return (
		<div className={cx("flex items-center gap-2", className)}>
			<input
				ref={input}
				type="file"
				id={inputId}
				name={name}
				accept={accept}
				disabled={disabled}
				aria-describedby={describedBy}
				onChange={(event) => choose(event.target.files?.[0] ?? null)}
				// Hidden from sight, not from the accessibility tree or the form. A
				// `display: none` input cannot be focused and is not submitted.
				className="sr-only"
			/>

			<Button asChild variant="secondary" size="sm">
				<label htmlFor={inputId} className={cx("cursor-pointer", FOCUS_RING)}>
					<Icon as={Paperclip} size={14} />
					{chooseLabel}
				</label>
			</Button>

			<span className="min-w-0 flex-1 truncate text-[12px] text-fg-muted">
				{chosen?.name ?? emptyLabel}
			</span>

			{chosen !== null && clearLabel !== undefined && (
				<Button
					size="sm"
					variant="ghost"
					onClick={() => {
						// The DOM node holds the selection; clearing state alone would
						// leave the same file un-choosable a second time.
						if (input.current !== null) {
							input.current.value = "";
						}
						choose(null);
					}}
				>
					{clearLabel}
				</Button>
			)}
		</div>
	);
}
